import { supabase } from './supabase'

/**
 * When the app is allowed to interrupt you, and when it must not.
 *
 * The daily summary is deliberately sent even on a day with nothing on it.
 * A summary that only arrives when there is news teaches you within a fortnight
 * that silence means nothing — and then you miss the day the app was broken
 * rather than the day that was quiet. "Niets te doen vandaag" is the message
 * that makes silence informative.
 */

export interface NotificationPrefs {
  digestAt: string
  digestOn: boolean
  quietFrom: string
  quietUntil: string
  weeklyOn: boolean
  /** 0 = Sunday. Null means no fixed day, which is a real choice. */
  runDay: number | null
}

export const DEFAULT_PREFS: NotificationPrefs = {
  digestAt: '08:30',
  digestOn: true,
  quietFrom: '22:00',
  quietUntil: '07:00',
  weeklyOn: true,
  runDay: null,
}

export async function loadPrefs(userId: string): Promise<NotificationPrefs> {
  const { data } = await supabase
    .from('notification_prefs')
    .select('digest_at, digest_on, quiet_from, quiet_until, weekly_on, run_day')
    .eq('user_id', userId)
    .maybeSingle()

  if (!data) return DEFAULT_PREFS
  return {
    digestAt: trimSeconds(data.digest_at as string),
    digestOn: Boolean(data.digest_on),
    quietFrom: trimSeconds(data.quiet_from as string),
    quietUntil: trimSeconds(data.quiet_until as string),
    weeklyOn: Boolean(data.weekly_on ?? true),
    runDay: data.run_day === null || data.run_day === undefined ? null : Number(data.run_day),
  }
}

export async function savePrefs(userId: string, prefs: NotificationPrefs): Promise<void> {
  const { error } = await supabase.from('notification_prefs').upsert(
    {
      user_id: userId,
      digest_at: prefs.digestAt,
      digest_on: prefs.digestOn,
      quiet_from: prefs.quietFrom,
      quiet_until: prefs.quietUntil,
      weekly_on: prefs.weeklyOn,
      run_day: prefs.runDay,
    },
    { onConflict: 'user_id' },
  )
  if (error) throw error
}

/** A preview of today's summary, so the setting is not an act of faith. */
export async function previewDigest(userId: string): Promise<string> {
  const { data, error } = await supabase.rpc('digest_body', { p_user_id: userId })
  if (error) throw error
  return (data as string) ?? ''
}

export const DAYS = [
  { value: 1, label: 'maandag' },
  { value: 2, label: 'dinsdag' },
  { value: 3, label: 'woensdag' },
  { value: 4, label: 'donderdag' },
  { value: 5, label: 'vrijdag' },
  { value: 6, label: 'zaterdag' },
  { value: 0, label: 'zondag' },
]

/** Postgres hands back 08:30:00; an <input type="time"> wants 08:30. */
function trimSeconds(value: string): string {
  return value?.slice(0, 5) ?? ''
}
